import React from 'react';
import { CarNameProps } from '@components/v2/TitleWithBenefits';
import { batch, useDispatch } from 'react-redux';
import { getModelTitle } from '../../../services/carList/utils/Car.utils';
import { useCarDetailsDuck } from '@hooks/useCarDetailsDuck';
import { useFeatureSwitchEnabled } from '@hooks/useFeatureSwitchEnabled';
import { FEATURES_LIST } from '../../../context/featureSwitchApp';
import UIDuck from '../../../redux/commonDucks/ui.duck';
import { Icons, Icon } from '@components/Icon';
import useTranslations from '@hooks/useTranslations';
import Link from '@components/Link';
import { Benefits } from '@components/Benefits';
import CarNumber from '@components/v2/CarNumber';
import { FUEL_CODES, isBrandAC } from '../../../constants/main';
import DemoVehicleLabel from '@components/DemoVehicleLabel';
import { useRouter } from 'next/router';
import Routes from '../../../constants/routes';

export const TitleWithBenefitsTemplate = ({
    className,
    engineInfo = true,
    isBasket = false,
    pipes = true,
    withBenefits = true,
    characteristicsOpen,
    onlyBenefits = false,
    isSpeckPackTitleBold = true,
    onlyTitle = false,
    onClick,
}: CarNameProps) => {
    const dispatch = useDispatch();
    const { carDetails } = useCarDetailsDuck();
    const { t } = useTranslations();
    const router = useRouter();

    const getFuel = () => carDetails?.fuel;
    const getGearbox = () => carDetails?.gearbox?.specs.find(({ key }: any) => key === 'type')?.label;
    const getPower = () => carDetails?.characteristics?.find(({ key }: any) => key === 'maximumPowerKW')?.value;

    const isBenefitsModeEnabled = useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_BENEFITS_MODE_ENABLED);

    const availableOffers = isBenefitsModeEnabled
        ? carDetails.offers?.filter((offer: any) => offer.benefits || offer.losangeLabel)
        : [];

    const scrollToCharacteristics = () => {
        const stockSlice = document.getElementById(characteristicsOpen ? 'carInfoBox' : 'characteristics');
        const y = stockSlice?.getBoundingClientRect()?.top + window?.pageYOffset - 100;
        window.scrollTo({ top: y, behavior: 'smooth' });
    };

    if (onlyBenefits) {
        return (
            <div className={`${className} titleWithBenefits`}>
                {availableOffers && Boolean(availableOffers.length) && <Benefits offers={carDetails.offers} />}
            </div>
        );
    }

    if (onlyTitle) {
        return (
            <div className={`${className} titleWithBenefits`}>
                <span className="title">{getModelTitle(carDetails, carDetails?.displayGrBodyStyleLabel)}</span>
            </div>
        );
    }

    const formattedPower = getPower() ? `- ${getPower()} ${t('configurator.info.power.label')}` : '';

    const formattedTitle =
        carDetails?.fuelCode === FUEL_CODES.ELECTRIC
            ? `${getFuel()} ${formattedPower}`
            : `${getFuel()} - ${getGearbox()} ${formattedPower}`;

    return (
        <div className={`${className} titleWithBenefits`}>
            <span className="title">{getModelTitle(carDetails, carDetails?.displayGrBodyStyleLabel)}</span>
            <div className="specs">
                <span className="specs-title">
                    {isSpeckPackTitleBold ? (
                        <strong>{carDetails.specPack.title}</strong>
                    ) : (
                        <span>{carDetails.specPack.title}</span>
                    )}
                    {pipes && ' |'}
                    &nbsp;
                    {formattedTitle}
                </span>
            </div>
            <CarNumber />
            {(router?.pathname?.includes(Routes.BASKET) || router?.pathname?.includes(Routes.SUMMARY)) &&
                carDetails?.VDStatus && (
                    <DemoVehicleLabel
                        status={carDetails?.VDStatus}
                        isBasket={router?.pathname?.includes(Routes.BASKET)}
                        isSummary={router?.pathname?.includes(Routes.SUMMARY)}
                        registrationDate={carDetails?.registrationDate}
                        mileage={carDetails?.mileage}
                    />
                )}
            <Link
                label={
                    characteristicsOpen
                        ? t('technicalFeatures.hideDetails')
                        : t('configurator.info.showCharacteristics')
                }
                dataTestId="TESTING_SHOW_CHARACTERISTICS_BUTTON"
                onClick={() => {
                    onClick?.();
                    characteristicsOpen
                        ? dispatch(UIDuck.closeCharacteristics())
                        : batch(() => {
                              scrollToCharacteristics();
                              dispatch(UIDuck.openCharacteristics({ data: {}, modalType: '' }));
                          });
                }}
                primary={!isBrandAC}
                secondary={isBrandAC}
                marginTop={10}
                withArrowIcon
            />
            {withBenefits && availableOffers && Boolean(availableOffers.length) && (
                <Benefits offers={carDetails.offers} />
            )}
        </div>
    );
};
